import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Activity, RefreshCw, Zap, ArrowLeft, Database } from "lucide-react";
import MoversMarquee from "../components/MoversMarquee";
import ConsensusCard from "../components/ConsensusCard";
import PickCard from "../components/PickCard";
import StockChart from "../components/StockChart";
import NewsPanel from "../components/NewsPanel";
import TickerSearch from "../components/TickerSearch";
import Disclaimer from "../components/Disclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EnnisDashboard() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [error, setError] = useState("");

  const loadScan = useCallback(async () => {
    setLoading(true);
    setError("");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.post(`${API}/ennis/scan`, {});
        if (res.data?.status === "ready" || res.data?.consensus) {
          setScan(res.data);
          setLoading(false);
          return;
        }
        // status: pending — keep polling
        if (attempts < 40) {
          setTimeout(poll, 4000);
        } else {
          setError("Scan timed out. Please try Re-scan.");
          setLoading(false);
        }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setLoading(false);
      }
    };
    poll();
  }, []);

  const loadSymbol = useCallback(async (sym) => {
    setDrillLoading(true);
    setError("");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.post(`${API}/ennis/analyze/${sym}`);
        if (res.data?.status === "ready" || res.data?.consensus) {
          setDrill(res.data);
          setDrillLoading(false);
          return;
        }
        if (attempts < 40) {
          setTimeout(poll, 4000);
        } else {
          setError(`Analysis of ${sym} timed out.`);
          setDrillLoading(false);
        }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
        setDrillLoading(false);
      }
    };
    poll();
  }, []);

  // Initial: load scan; if /s/:symbol, also load symbol
  useEffect(() => {
    loadScan();
  }, [loadScan]);

  useEffect(() => {
    if (symbol) {
      loadSymbol(symbol);
    } else {
      setDrill(null);
    }
  }, [symbol, loadSymbol]);

  const handleSearch = (sym) => {
    navigate(`/s/${sym}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0A0C] sticky top-0 z-30">
        <div className="px-6 sm:px-8 py-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-white/20 flex items-center justify-center bg-black">
              <Zap className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                Multi-Model AI · Market Console
              </span>
              <h1
                className="font-display text-2xl font-black tracking-tighter text-white"
                data-testid="app-title"
              >
                ENNIS
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <TickerSearch onSearch={handleSearch} disabled={loading || drillLoading} />
            <button
              onClick={loadScan}
              disabled={loading}
              data-testid="rescan-button"
              className="flex items-center gap-2 border border-white/20 hover:border-white hover:bg-white/5 px-4 py-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
                strokeWidth={2}
              />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                {loading ? "Scanning" : "Re-scan"}
              </span>
            </button>
          </div>
        </div>

        <MoversMarquee
          gainers={scan?.scan?.top_gainers || []}
          losers={scan?.scan?.top_losers || []}
          active={scan?.scan?.most_actively_traded || []}
        />
      </header>

      <main className="flex-1 px-6 sm:px-8 py-8">
        {/* If drilling into a symbol, show that view */}
        {symbol ? (
          <SymbolView
            symbol={symbol}
            drill={drill}
            loading={drillLoading}
            error={error}
            onBack={() => navigate("/")}
          />
        ) : (
          <DashboardView
            scan={scan}
            loading={loading}
            error={error}
            onPick={handleSearch}
          />
        )}
      </main>

      <Disclaimer />
    </div>
  );
}

function DashboardView({ scan, loading, error, onPick }) {
  if (loading && !scan) {
    return <FullLoader label="Running multi-model market scan" />;
  }
  if (error) {
    return <ErrorBanner message={error} />;
  }
  if (!scan) return null;

  const rateLimited = scan.scan?.rate_limited;

  return (
    <div className="flex flex-col gap-8 max-w-[1400px] mx-auto reveal">
      {/* Status strip */}
      <div className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-[#121215] px-4 py-3">
        <div className="flex items-center gap-3">
          <Activity className="w-4 h-4 text-emerald-400" strokeWidth={2} />
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
            Last Scan · {scan.scan?.as_of?.split(" ")[0] || "—"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-zinc-500">
          <Database className="w-3.5 h-3.5" strokeWidth={2} />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
            Source: Alpha Vantage
          </span>
        </div>
      </div>

      {rateLimited && (
        <div className="border border-yellow-500/30 bg-yellow-500/5 px-4 py-3" data-testid="rate-limit-banner">
          <p className="font-mono text-xs text-yellow-300 leading-relaxed">
            ⚠ Alpha Vantage free tier limit hit. {scan.scan.message}
          </p>
        </div>
      )}

      {/* Consensus + Picks */}
      <ConsensusCard models={scan.models || []} consensus={scan.consensus || []} />

      <section data-testid="picks-grid">
        <div className="flex items-end justify-between mb-4">
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
              Ranked Plays
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white">
              Today's AI Picks
            </h2>
          </div>
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase">
            Click any pick to drill in
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(scan.consensus || []).slice(0, 6).map((pick, i) => (
            <PickCard key={pick.symbol} pick={pick} rank={i + 1} onSelect={onPick} />
          ))}
          {(!scan.consensus || scan.consensus.length === 0) && (
            <div className="col-span-full border border-dashed border-white/10 bg-[#121215] p-8 text-center">
              <p className="font-mono text-xs text-zinc-500">
                No consensus picks returned. The models may be rate-limited or the market quiet. Try re-scanning shortly.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Movers + News */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MoversCol title="Top Gainers" items={scan.scan?.top_gainers || []} tone="up" onPick={onPick} />
          <MoversCol title="Most Active" items={scan.scan?.most_actively_traded || []} tone="active" onPick={onPick} />
          <MoversCol title="Top Losers" items={scan.scan?.top_losers || []} tone="down" onPick={onPick} />
        </div>
        <NewsPanel items={scan.news || []} />
      </section>
    </div>
  );
}

function MoversCol({ title, items, tone, onPick }) {
  const accent =
    tone === "up"
      ? "text-green-400"
      : tone === "down"
      ? "text-red-400"
      : "text-sky-300";
  return (
    <div className="border border-white/10 bg-[#121215] p-4" data-testid={`movers-col-${tone}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-400 uppercase">
          {title}
        </span>
        <span className={`font-mono text-[10px] ${accent}`}>●</span>
      </div>
      <div className="flex flex-col divide-y divide-white/5">
        {items.slice(0, 5).map((m, i) => {
          const pct = parseFloat(String(m.change_percentage || "0").replace("%", ""));
          const up = pct >= 0;
          return (
            <button
              key={`${m.ticker}-${i}`}
              onClick={() => onPick(m.ticker)}
              className="py-2 flex items-center justify-between hover:bg-white/[0.03] px-1 -mx-1 transition-colors text-left"
              data-testid={`mover-${tone}-${m.ticker}`}
            >
              <div className="flex flex-col">
                <span className="font-mono font-bold text-sm text-white">{m.ticker}</span>
                <span className="font-mono text-[10px] text-zinc-500">
                  Vol {abbreviateNum(m.volume)}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="font-mono text-sm text-white tabular-nums">
                  ${parseFloat(m.price || 0).toFixed(2)}
                </span>
                <span
                  className={`font-mono text-[10px] tabular-nums ${
                    up ? "text-green-400" : "text-red-400"
                  }`}
                >
                  {up ? "+" : ""}
                  {pct.toFixed(2)}%
                </span>
              </div>
            </button>
          );
        })}
        {items.length === 0 && (
          <span className="font-mono text-[10px] text-zinc-500 py-2">— no data —</span>
        )}
      </div>
    </div>
  );
}

function SymbolView({ symbol, drill, loading, error, onBack }) {
  if (loading) return <FullLoader label={`Analyzing ${symbol}`} />;
  if (error) return <ErrorBanner message={error} />;
  if (!drill) return null;

  const q = drill.quote || {};
  const quoteRateLimited = q.rate_limited || !q.price;
  const pct = parseFloat(String(q.change_percent || "0").replace("%", ""));
  const up = pct >= 0;

  return (
    <div className="flex flex-col gap-6 max-w-[1400px] mx-auto reveal">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Back to Scan</span>
        </button>
      </div>

      {quoteRateLimited && (
        <div className="border border-yellow-500/30 bg-yellow-500/5 px-4 py-3" data-testid="quote-rate-limit-banner">
          <p className="font-mono text-xs text-yellow-300 leading-relaxed">
            ⚠ Live quote unavailable — Alpha Vantage free tier limit. Chart, news, and AI analysis below
            use the last successfully cached data.
          </p>
        </div>
      )}

      <div className="border border-white/15 bg-[#121215] p-6 flex flex-wrap items-end gap-8" data-testid="symbol-header">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
            Ticker
          </span>
          <div className="font-display text-5xl font-black tracking-tighter text-white">
            {drill.symbol}
          </div>
        </div>
        {!quoteRateLimited && (
          <>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Price</span>
              <div className="font-mono text-3xl font-medium text-white tabular-nums">
                ${parseFloat(q.price || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Change</span>
              <div className={`font-mono text-xl ${up ? "text-green-400" : "text-red-400"}`}>
                {up ? "▲" : "▼"} {pct.toFixed(2)}%
              </div>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Volume</span>
              <div className="font-mono text-xl text-white tabular-nums">
                {abbreviateNum(q.volume)}
              </div>
            </div>
          </>
        )}
        {quoteRateLimited && (
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Price</span>
            <div className="font-mono text-xl text-zinc-500 tabular-nums">— unavailable —</div>
          </div>
        )}
      </div>

      <ConsensusCard models={drill.models || []} consensus={drill.consensus || []} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <StockChart candles={drill.chart?.candles || []} symbol={drill.symbol} />
        </div>
        <NewsPanel items={(drill.news || []).slice(0, 12)} title={`${drill.symbol} Headlines`} />
      </div>
    </div>
  );
}

function FullLoader({ label }) {
  return (
    <div className="max-w-[900px] mx-auto mt-10">
      <div className="scan-loader border border-white/10 bg-[#121215] p-10 flex flex-col items-center gap-4" data-testid="loader">
        <Zap className="w-8 h-8 text-white animate-pulse" strokeWidth={1.6} />
        <p className="font-mono text-xs tracking-[0.3em] text-zinc-300 uppercase">{label}…</p>
        <p className="font-mono text-[10px] text-zinc-500">
          Fetching movers → pulling news → running Claude + GPT + Gemini in parallel
        </p>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div
      className="max-w-[900px] mx-auto mt-10 border border-red-500/30 bg-red-500/5 p-6"
      data-testid="error-banner"
    >
      <p className="font-mono text-xs tracking-wider text-red-300 uppercase mb-2">Error</p>
      <p className="text-sm text-zinc-200">{message}</p>
    </div>
  );
}

function abbreviateNum(n) {
  const num = parseFloat(n || 0);
  if (!num) return "—";
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num);
}
